import { SupabaseClient } from '@supabase/supabase-js';
import { Database } from '@/lib/supabase/types';

type ShortLink = Database['public']['Tables']['short_links']['Row'];
type _ShortLinkInsert = Database['public']['Tables']['short_links']['Insert'];

/**
 * Characters used for generating short codes
 * Excludes ambiguous characters (0, O, l, 1, I) for better readability
 */
const CHARSET = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789';

/**
 * Generate a random short code
 * @param length - Length of the code (default: 6)
 * @returns Random alphanumeric code
 */
function generateShortCode(length: number = 6): string {
  let code = '';
  for (let i = 0; i < length; i++) {
    code += CHARSET.charAt(Math.floor(Math.random() * CHARSET.length));
  }
  return code;
}

/**
 * Create a new short link
 */
export async function createShortLink(
  supabase: SupabaseClient<Database>,
  params: {
    originalUrl: string;
    title?: string;
    customCode?: string;
    contentId?: string;
    expiresAt?: Date;
    userId: string;
  }
): Promise<{ data: ShortLink | null; error: Error | null }> {
  const { originalUrl, title, customCode, contentId, expiresAt, userId } = params;

  // Generate or use custom code
  let code = customCode || generateShortCode();

  // If custom code, check if it's available
  if (customCode) {
    const { data: existing } = await supabase
      .from('short_links')
      .select('code')
      .eq('code', customCode)
      .single();

    if (existing) {
      return { data: null, error: new Error('This custom code is already taken') };
    }
  } else {
    // Generate unique code with retry logic
    let attempts = 0;
    const maxAttempts = 5;

    while (attempts < maxAttempts) {
      const { data: existing } = await supabase
        .from('short_links')
        .select('code')
        .eq('code', code)
        .single();

      if (!existing) break;

      code = generateShortCode();
      attempts++;
    }

    if (attempts >= maxAttempts) {
      return { data: null, error: new Error('Failed to generate unique code') };
    }
  }

  const { data, error } = await supabase
    .from('short_links')
    .insert({
      code,
      original_url: originalUrl,
      title: title || null,
      content_id: contentId || null,
      expires_at: expiresAt?.toISOString() || null,
      created_by: userId,
    })
    .select()
    .single();

  if (error) {
    return { data: null, error: new Error(error.message) };
  }

  return { data, error: null };
}

/**
 * Get or create a short link for a URL
 * Returns existing link if one already exists for the same URL and content
 */
export async function getOrCreateShortLink(
  supabase: SupabaseClient<Database>,
  params: {
    originalUrl: string;
    title?: string;
    contentId?: string;
    userId: string;
  }
): Promise<{ data: ShortLink | null; error: Error | null }> {
  const { originalUrl, contentId } = params;

  // First, check if a short link already exists for this URL and content
  let query = supabase
    .from('short_links')
    .select('*')
    .eq('original_url', originalUrl)
    .eq('is_active', true);

  // If contentId is provided, also match on that
  if (contentId) {
    query = query.eq('content_id', contentId);
  }

  // Use limit(1) instead of single() to avoid errors when multiple or no rows exist
  const { data: existingLinks } = await query.limit(1);

  if (existingLinks && existingLinks.length > 0) {
    return { data: existingLinks[0], error: null };
  }

  // No existing link, create a new one
  return createShortLink(supabase, params);
}

/**
 * Build the full short URL
 */
export function buildShortUrl(code: string, baseUrl?: string): string {
  const base = (baseUrl || process.env.NEXT_PUBLIC_APP_URL || '').replace(/\/+$/, '');
  return `${base}/s/${code}`;
}

/**
 * URL regex pattern for finding URLs in text
 */
const URL_REGEX = /https?:\/\/[^\s<>"']+/gi;

/**
 * Check if a URL should be shortened
 * Skip internal short links to avoid infinite loops
 */
function shouldShortenUrl(url: string, _baseUrl: string): boolean {
  // Don't shorten already-shortened links
  if (url.includes('/s/')) return false;

  // Don't shorten mailto: or tel: links
  if (url.startsWith('mailto:') || url.startsWith('tel:')) return false;

  // Don't shorten data: URLs
  if (url.startsWith('data:')) return false;

  // URL must be at least 30 characters to be worth shortening
  if (url.length < 30) return false;

  return true;
}

/**
 * Process HTML content and replace URLs with short links
 * Used for email campaigns to enable click tracking
 */
export async function shortenUrlsInHtml(
  supabase: SupabaseClient<Database>,
  html: string,
  params: {
    userId: string;
    contentId?: string;
    titlePrefix?: string;
  }
): Promise<string> {
  const { userId, contentId, titlePrefix = 'Email link' } = params;
  const baseUrl = process.env.NEXT_PUBLIC_APP_URL || '';

  // Find all URLs in the HTML
  const urls = html.match(URL_REGEX) || [];

  // Deduplicate URLs
  const uniqueUrls = [...new Set(urls)].filter(url => shouldShortenUrl(url, baseUrl));

  if (uniqueUrls.length === 0) {
    return html;
  }

  // Create short links for each unique URL
  const urlMap = new Map<string, string>();

  for (const originalUrl of uniqueUrls) {
    try {
      // Check if short link already exists for this URL
      const { data: existingLink } = await supabase
        .from('short_links')
        .select('code')
        .eq('original_url', originalUrl)
        .eq('is_active', true)
        .single();

      if (existingLink) {
        urlMap.set(originalUrl, buildShortUrl(existingLink.code));
      } else {
        // Create new short link
        const { data: newLink } = await createShortLink(supabase, {
          originalUrl,
          title: `${titlePrefix}: ${originalUrl.substring(0, 50)}...`,
          contentId,
          userId
        });

        if (newLink) {
          urlMap.set(originalUrl, buildShortUrl(newLink.code));
        }
      }
    } catch (error) {
      console.error(`Failed to shorten URL: ${originalUrl}`, error);
      // Keep original URL on failure
    }
  }

  // Replace URLs in HTML
  let processedHtml = html;
  for (const [originalUrl, shortUrl] of urlMap.entries()) {
    // Replace in href attributes
    processedHtml = processedHtml.replace(
      new RegExp(`href=["']${escapeRegex(originalUrl)}["']`, 'g'),
      `href="${shortUrl}"`
    );

    // Replace plain URLs (not in attributes)
    // This handles URLs in text content
    processedHtml = processedHtml.replace(
      new RegExp(`(?<!href=["'])${escapeRegex(originalUrl)}(?!["'])`, 'g'),
      shortUrl
    );
  }

  return processedHtml;
}

/**
 * Escape special characters for use in regex
 */
function escapeRegex(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
